import { useState, useEffect, useMemo, useRef } from 'react'
import MetricCard from '../components/MetricCard'
import AgentSkillsModal from '../components/AgentSkillsModal'
import Icon from '../components/Icon'
import TeamMemberRow from './TeamMemberRow'
import { fetchTeamMetrics, fetchTeamMembers } from '../api/team'
import styles from './Team.module.css'

// ---------------------------------------------------------------------------
// Team page
// ---------------------------------------------------------------------------
export default function Team() {
  const [metrics,     setMetrics]     = useState([])
  const [members,     setMembers]     = useState([])
  const [teams,       setTeams]       = useState([])
  const [skills,      setSkills]      = useState([])
  const [loading,     setLoading]     = useState({ metrics: true, members: true })
  const [error,       setError]       = useState(null)
  const [search,      setSearch]      = useState('')
  const [teamFilter,  setTeamFilter]  = useState('all')
  const [skillFilter, setSkillFilter] = useState('all')
  const [detailAgent, setDetailAgent] = useState(null)
  const [detailMode,  setDetailMode]  = useState('details')

  // The ref guards below suppress React 18 StrictMode's intentional double-invoke of
  // effects in dev (the source of the duplicate requests); a real remount on navigation
  // gets a fresh fiber, so the page still refetches when you return to it.
  const didFetchMetrics = useRef(false)
  useEffect(() => {
    if (didFetchMetrics.current) return
    didFetchMetrics.current = true
    fetchTeamMetrics()
      .then(data => setMetrics(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(prev => ({ ...prev, metrics: false })))
  }, [])

  // One request backs both the team selector and the member list.
  const didFetch = useRef(false)
  useEffect(() => {
    if (didFetch.current) return
    didFetch.current = true
    fetchTeamMembers()
      .then(data => {
        setTeams(data.teams ?? [])
        setSkills(data.skills ?? [])
        setMembers(data.members ?? [])
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(prev => ({ ...prev, members: false })))
  }, [])

  // Team names arrive once in `teams`; re-attach them to each member's memberships so
  // the row and detail modal can render names without the server repeating them.
  const teamsById = useMemo(
    () => new Map(teams.map(t => [t.id, t.name])),
    [teams],
  )
  const enrichedMembers = useMemo(
    () => members.map(m => ({
      ...m,
      // Drop teams whose id doesn't resolve to a name in the `teams` lookup —
      // rendering a nameless chip leaves an empty pill. A member left with no
      // resolvable teams falls back to "Unassigned" in TeamMemberRow.
      teams: m.teams
        .map(t => ({ ...t, name: teamsById.get(t.id) }))
        .filter(t => t.name),
    })),
    [members, teamsById],
  )

  const totalNewSkills = enrichedMembers.reduce((sum, m) => sum + m.new_skills, 0)

  // A proposed skill was approved/rejected inside the modal — drop this member's
  // review count by one. The badge, header total and top-of-list ordering all derive
  // from `members`, so they update together (and the row flips Review→Details at 0).
  const handleProposedResolved = (agentId) =>
    setMembers(prev => prev.map(m =>
      m.id === agentId ? { ...m, new_skills: Math.max(0, (m.new_skills ?? 0) - 1) } : m,
    ))

  // A member was disabled inside the modal — drop them from the list now rather
  // than waiting for a refetch (the server already excludes inactive agents).
  function handleAgentDisabled(agentId) {
    setMembers(prev => prev.filter(m => m.id !== agentId))
  }

  const filtered = enrichedMembers.filter(m => {
    // Prefix-match the term against each name token, so "ma" matches the
    // start of a first or last name (Marie, Mass, Marselis) but not a mid-word
    // occurrence (Kumar, Poornima).
    const term = search.trim().toLowerCase()
    const matchesSearch =
      term === '' ||
      m.name.toLowerCase().split(/\s+/).some(part => part.startsWith(term))
    const matchesTeam   = teamFilter === 'all' || m.teams.some(t => String(t.id) === teamFilter)
    // A member matches when they already hold the skill or have a pending proposal
    // for it — the server folds both into `skill_ids`.
    const matchesSkill  = skillFilter === 'all' || (m.skill_ids ?? []).some(id => String(id) === skillFilter)
    return matchesSearch && matchesTeam && matchesSkill
  })

  // Members with skills awaiting review float to the top of the list; everyone
  // else keeps the server's order. Array.sort is stable, so this is a pure
  // partition — and because it runs after `filtered`, a member excluded by
  // search can't be floated up, and clearing search re-floats them to the top.
  const ordered = [...filtered].sort(
    (a, b) => (b.new_skills > 0) - (a.new_skills > 0),
  )

  return (
    <div className={styles.page}>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* ── Page header ─────────────────────────────── */}
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Team Overview</h1>
        <p className={styles.pageSub}>
          GaussIQ has identified {totalNewSkills} new skills to add to your team members
        </p>
      </div>

      {/* ── Metrics row ─────────────────────────────── */}
      {/* The bar-chart tile (Skill Levels) gets a wider slot than the number
          tiles beside it — its two-column bars need more horizontal room than
          a single number does. */}
      <div className={styles.metricsRow}>
        {loading.metrics
          ? Array(4).fill(0).map((_, i) => <div key={i} className={styles.metricSkeleton} />)
          : metrics.map(m => (
              <div
                key={m.name}
                className={m.cardtype === 'bar' ? styles.metricBarSlot : styles.metricSlot}
              >
                <MetricCard
                  name={m.name}
                  value={m.value}
                  description={m.description}
                  cardtype={m.cardtype}
                  icon={m.icon}
                  evidenceHeader={m.evidence_header}
                  evidence={m.evidence}
                  barColumns={2}
                  narrow={m.cardtype !== 'bar'}
                />
              </div>
            ))
        }
      </div>

      {/* ── Team Members list ────────────────────────── */}
      <div className={styles.membersSection}>
        <div className={styles.membersHeader}>
          <div className={styles.membersTitleRow}>
            <span className={styles.membersIcon}>
              <Icon name="people" />
            </span>
            <h2 className={styles.sectionTitle}>Team Members</h2>
          </div>
          <div className={styles.searchWrap}>
            <input
              className={styles.memberSearch}
              type="text"
              placeholder="Search for Team Member"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setSearch('')}
            />
            {search && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => setSearch('')}
              >
                Clear
              </button>
            )}
          </div>
          <div className={styles.filters}>
            <select
              className={styles.filterSelect}
              value={teamFilter}
              onChange={e => setTeamFilter(e.target.value)}
            >
              <option value="all">All Teams</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select
              className={styles.filterSelect}
              value={skillFilter}
              onChange={e => setSkillFilter(e.target.value)}
              aria-label="Filter by skill"
            >
              <option value="all">All Skills</option>
              {skills.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>

        <div className={styles.memberList}>
          {loading.members
            ? Array(4).fill(0).map((_, i) => <div key={i} className={styles.memberSkeleton} />)
            : ordered.length === 0
              ? <div className={styles.emptyState}>No team members match your filters.</div>
              : ordered.map(m => (
                  <TeamMemberRow
                    key={m.id}
                    member={m}
                    onOpen={() => {
                      setDetailMode(m.new_skills > 0 ? 'review' : 'details')
                      setDetailAgent(m)
                    }}
                  />
                ))
          }
        </div>
      </div>

      {detailAgent && (
        <AgentSkillsModal
          agentId={detailAgent.id}
          member={detailAgent}
          mode={detailMode}
          onClose={() => setDetailAgent(null)}
          onProposedResolved={handleProposedResolved}
          onAgentDisabled={handleAgentDisabled}
        />
      )}

    </div>
  )
}
